import Decimal from 'decimal.js';

import { type DollarsString, parseDollarsString } from 'models/dollars';

const pluralize = (text: string, count: number, pluralText?: string): string => {
    if (pluralText !== undefined) {
        return count === 1 ? text : pluralText;
    }
    return text + (count === 1 ? '' : 's');
};

const toTitleCase = (text: string): string => {
    const splitText = text.trim().split(' ');
    if (splitText.length > 0) {
        return splitText
            .filter((w) => w.length > 0)
            .map((w) => {
                if (w.length > 1) {
                    return w[0].toUpperCase() + w.slice(1);
                }
                return w.toUpperCase();
            })
            .join(' ');
    }
    return text;
};

const lowerFirstLetter = (text: string) => (!!text ? `${text.charAt(0).toLowerCase()}${text.slice(1)}` : '');

const upperFirstLetter = (text: string) => (!!text ? `${text.charAt(0).toUpperCase()}${text.slice(1)}` : '');

const asCents = (value: number): string => {
    if (value % 1 >= 0.05 && value % 1 < 0.95) {
        // Avoid catching floating point errors
        return `${value.toFixed(1)}¢`;
    }

    return `${Math.round(value)}¢`;
};

const asCentsUnrounded = (value: number): string => {
    // Trim trailing zeros (and a now-bare decimal point) BEFORE appending the
    // unit. Doing it after appended `¢` made `endsWith('0')` always false, so
    // values like 99.5 rendered as "99.50¢" rather than "99.5¢".
    const trimmed = value.toFixed(2).replace(/\.?0+$/, '');
    return `${trimmed}¢`;
};

const bidAsCents = (bidCents: number, bidDollars?: DollarsString): string => {
    if (bidDollars !== undefined && bidDollars !== null) {
        const cents = parseDollarsString(bidDollars).times(100).toNumber();
        return asCents(cents);
    }
    return asCents(bidCents);
};

const askFromBidAsCents = (bidCents: number, bidDollars?: DollarsString): string => {
    if (bidDollars !== undefined && bidDollars !== null) {
        const bid = parseDollarsString(bidDollars);
        const askCents = bid.neg().plus(1).times(100).toNumber();
        return asCents(askCents);
    }
    return asCents(100 - bidCents);
};

/** Returns true if bid has a positive value (checks dollars first, falls back to cents) */
const hasBid = (bidCents: number, bidDollars?: DollarsString): boolean => {
    if (bidDollars !== undefined && bidDollars !== null) {
        return parseDollarsString(bidDollars).gt(0);
    }
    return bidCents > 0;
};

/** Returns bid value in dollars for comparison (uses dollars if available) */
const getBidDollars = (bidCents: number, bidDollars?: DollarsString): number => {
    if (bidDollars !== undefined && bidDollars !== null) {
        return parseDollarsString(bidDollars).toNumber();
    }
    return bidCents / 100;
};

const moneyFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});

const integerMoneyFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
});

const largeMoneyFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 4,
    minimumFractionDigits: 0,
});

const compactMoneyFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    notation: 'compact',
    compactDisplay: 'short',
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
    // Keep whole compact values as "$5M" rather than "$5.0M"; the trailing-zero
    // output of compact currency is ICU-version-dependent (Node 22's ICU 76 vs
    // Node 24's ICU 77), so pin it for consistency across runtimes.
    trailingZeroDisplay: 'stripIfInteger',
});

const toMoney = (val: number, includeCents = true): string => {
    const modifiedVal = Object.is(val, -0) ? 0 : val;
    return includeCents ? moneyFormatter.format(modifiedVal) : integerMoneyFormatter.format(modifiedVal);
};

// Takes cents, unlike the dollar-taking helpers here, and renders `--` for an
// unknown amount rather than fabricating a figure. Whole dollars only.
const toMoneyWithFallback = (cents: number | null): string => (cents === null ? '--' : toMoney(cents / 100, false));

const toMoneyRoundedUpToCents = (val: number, includeCents = true): string => {
    return toMoney(Math.ceil(val * 100) / 100, includeCents);
};

// Floors `val` to whole cents (rounds toward zero). Use for payout / cash-out
// displays where rounding up would overstate what the user receives. Uses
// Decimal so floating-point error never pulls a value below the cent boundary
// it's notionally on (e.g. 0.29 stored as 0.28999… still floors to 0.29).
const roundCentsDown = (val: number): number => {
    return new Decimal(val).toDecimalPlaces(2, Decimal.ROUND_DOWN).toNumber();
};

const toMoneyRoundedDownToCents = (val: number, includeCents = true): string => {
    return toMoney(roundCentsDown(val), includeCents);
};

const toMoneyLarge = (val: number): string => {
    return largeMoneyFormatter.format(val);
};

const toCompactMoney = (val: number): string => {
    return compactMoneyFormatter.format(val).toLowerCase();
};

const toMoneyNoCents = (val: number): string => {
    return integerMoneyFormatter.format(trimDecimals(val));
};
const toMoneyNoCentsIfZero = (val: number): string => {
    return val === 0 ? toMoneyNoCents(val) : toMoney(val);
};

const toMoneyWithCentsIfCents = (val: number): string => {
    const modifiedVal = Object.is(val, -0) ? 0 : val;
    const hasCents = modifiedVal % 1 !== 0;

    if (hasCents) {
        return moneyFormatter.format(modifiedVal);
    }
    return integerMoneyFormatter.format(modifiedVal);
};

const toMoneyWithCentsIfCentsWithSign = (val: number): string => {
    return (val > 0 ? '+' : val < 0 ? '-' : '') + toMoneyWithCentsIfCents(Math.abs(val));
};

// Rounds a dollar value to whole cents with ties broken toward zero
// (round-half-down). Web's Intl/`toMoney*` formatters round ties away from zero,
// but the mobile app's NumberFormat rounds them toward zero — so a half-cent
// figure (e.g. $0.115) renders a cent higher on web ($0.12) than in the app
// ($0.11). Wrap fee-inclusive portfolio values (payouts, returns, cost-with-fees)
// in this before formatting so the two clients agree. Only exact ties change;
// non-tie values are unaffected, and it's float-safe (a true $0.12 that drifts to
// 0.11999999 still formats as $0.12, not $0.11).
const roundCentsHalfDown = (val: number): number => {
    return new Decimal(val).toDecimalPlaces(2, Decimal.ROUND_HALF_DOWN).toNumber();
};

// Convenience wrapper: round-half-down to cents, then format showing cents only
// when the value isn't a whole dollar. See `roundCentsHalfDown`.
const toMoneyWithCentsIfCentsHalfDown = (val: number): string => {
    return toMoneyWithCentsIfCents(roundCentsHalfDown(val));
};

// Payout analog of `toMoneyWithCentsIfCentsHalfDown`: floor to cents (always
// round down), then format showing cents only when the value isn't a whole
// dollar. Use for payout displays — a payout is real money credited in whole
// cents and is never rounded up in the user's favor, so a fee-proration figure
// like $1.236 is shown (and paid) as $1.23, matching the mobile app.
const toMoneyWithCentsIfCentsRoundedDown = (val: number): string => {
    return toMoneyWithCentsIfCents(roundCentsDown(val));
};

const toPriceInCents = (valueDollars: Decimal): string => {
    const baseString = valueDollars.times(100).toString();
    return `${baseString}¢`;
};

const trimDecimals = (val: number): number => {
    return Math.trunc(val);
};

const trimCentimals = (val: number): number => {
    return Math.trunc(val * 100) / 100;
};

// We should not compare numbers to 0, but instead do epsilon checking because of floating point error
// This function returns the value of (val == comparison)
const equalTo = (val: number, comparison: number): boolean => {
    return Math.abs(val - comparison) < Number.EPSILON;
};

// This function returns the value of (val >= comparison)
const greaterThanOrEqualTo = (val: number, comparison: number): boolean => {
    // Check if they're "the same number", if not, just return the answer
    return equalTo(val, comparison) || val >= comparison;
};

const toMoneyWithSign = (val: number, withSpace = false, includeCents = true): string => {
    return (val > 0 ? '+' : val < 0 ? '-' : '') + (withSpace ? ' ' : '') + toMoney(Math.abs(val), includeCents);
};

const toMoneyCentsIfNotZero = (val: number): string => {
    // Include cents if not equals 00
    const includeCents = val % 1 !== 0;
    return toMoney(val, includeCents);
};

const toMoneyCentsIfNotZeroWithSign = (val: number): string => {
    // Include cents if not equals 00
    const includeCents = val % 1 !== 0;
    return toMoneyWithSign(val, false, includeCents);
};

const toNumber = (val: number, precision = 2): string => {
    const numberFormatter = new Intl.NumberFormat('en-US', {
        minimumFractionDigits: precision,
        maximumFractionDigits: precision,
    });
    return numberFormatter.format(val);
};

const toContracts = (val: number, decimals = 0): string => toVariableLengthFloat(val, decimals);

const toVariableLengthFloat = (val: number, precision = 2, minPrecision = 0): string => {
    const numberFormatter = new Intl.NumberFormat('en-US', {
        minimumFractionDigits: minPrecision,
        maximumFractionDigits: precision,
    });
    return numberFormatter.format(val);
};

const toRoundedMoney = (val: number, precision?: number): string => {
    let rounded = toMoney(val, true);

    if (Math.abs(val) >= 1000000) {
        rounded = (val < 0 ? '-' : '') + '$' + toVariableLengthFloat(Math.abs(val) / 1000000, precision) + 'M';
    } else if (Math.abs(val) >= 10000) {
        rounded = (val < 0 ? '-' : '') + '$' + toVariableLengthFloat(Math.abs(val) / 1000, precision) + 'K';
    } else if (Math.abs(val) >= 1000) {
        rounded = toMoney(val, false);
    }

    return rounded;
};

const toRounded = (val: number, precision?: number): string => {
    let rounded = (val < 0 ? '-' : '') + toVariableLengthFloat(Math.abs(val), precision);

    if (Math.abs(val) >= 1000000) {
        rounded = (val < 0 ? '-' : '') + toVariableLengthFloat(Math.abs(val) / 1000000, precision) + 'M';
    } else if (Math.abs(val) >= 10000) {
        rounded = (val < 0 ? '-' : '') + toVariableLengthFloat(Math.abs(val) / 1000, precision) + 'K';
    }

    return rounded;
};

const numberToUnitString = (val: number): string => {
    if (val >= 1e6 && val % 1e4 === 0) {
        return toVariableLengthFloat(val / 1e6) + 'M';
    } else if (val >= 1e3 && val % 10 === 0) {
        return toVariableLengthFloat(val / 1e3) + 'K';
    }

    return toVariableLengthFloat(val, 4);
};

const numberToCompactUnitString = (val: number): string => {
    if (val >= 1e6) {
        return toVariableLengthFloat(val / 1e6, 0) + 'M';
    } else if (val >= 1e4) {
        return toVariableLengthFloat(val / 1e3, 0) + 'K';
    }

    return numberToUnitString(val);
};

const toCentsInteger = (valueInDollars: number): number => {
    return Math.round(valueInDollars * 100);
};

const toFormattedCents = (valueInDollars: number): string => {
    return asCents(toCentsInteger(valueInDollars));
};

/*
  toPercentage receives a percentual value between 1 and 0
  the input shouldn't be multiplied by 100 before calling toPercentage
 */

//   TODO: split this logic in subsequent commits for PRO-417
const toPercentage = (value: number, withSign: boolean, withSpace = false, precision = 2): string =>
    `${withSign && value >= 0.0 ? '+' : ''}${withSpace ? ' ' : ''}${(value * 100).toFixed(precision)}%`;

const toPercentageFixed = (value: number) => {
    const parsedValue: number = Math.abs(value) < 1 ? Math.abs(value) : value;
    return `${toCommas(parseInt(parsedValue.toFixed()), 0)}%`;
};

// avoid creating a new Intl.NumberFormat object for each call
const commasFormatterCache = new Map<number, Intl.NumberFormat>();

function toCommas(x: number, maxDecimalPlaces: number): string {
    let fmt = commasFormatterCache.get(maxDecimalPlaces);
    if (!fmt) {
        fmt = new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 0,
            maximumFractionDigits: maxDecimalPlaces,
        });
        commasFormatterCache.set(maxDecimalPlaces, fmt);
    }
    return fmt.format(x);
}

export {
    pluralize,
    toTitleCase,
    upperFirstLetter,
    toMoney,
    toMoneyWithFallback,
    toMoneyRoundedUpToCents,
    toMoneyRoundedDownToCents,
    toMoneyLarge,
    toCompactMoney,
    toMoneyCentsIfNotZero,
    toMoneyCentsIfNotZeroWithSign,
    toRounded,
    toRoundedMoney,
    toMoneyWithSign,
    toPercentage,
    toPercentageFixed,
    toNumber,
    toContracts,
    toVariableLengthFloat,
    toCentsInteger,
    toFormattedCents,
    toCommas,
    toPriceInCents,
    trimCentimals,
    numberToUnitString,
    numberToCompactUnitString,
    asCents,
    asCentsUnrounded,
    bidAsCents,
    askFromBidAsCents,
    hasBid,
    getBidDollars,
    greaterThanOrEqualTo,
    lowerFirstLetter,
    toMoneyWithCentsIfCents,
    toMoneyWithCentsIfCentsHalfDown,
    roundCentsHalfDown,
    roundCentsDown,
    toMoneyWithCentsIfCentsRoundedDown,
    toMoneyWithCentsIfCentsWithSign,
    toMoneyNoCentsIfZero,
};
